import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge, validators } from 'tailwind-merge/es5';

const isKebabNumber = (classPart: string) => /^(\d+-)*\d+$/.test(classPart);

const twMerge = extendTailwindMerge({
  override: {
    classGroups: {
      'font-size': [
        {
          text: [
            validators.isNumber,
            validators.isArbitraryLength,
            isKebabNumber,
            (classPart: string) => /size-inherit/.test(classPart),
          ],
        },
      ],
    },
    conflictingClassGroups: {
      // In our tailwind.config we override the default font-size scale without line-height
      // I.e. font-size should no longer conflict with line-height
      // https://tailwindcss.com/docs/font-size
      'font-size': [],
    },
  },
  extend: {
    classGroups: {
      'text-alignment': [{ text: ['initial'] }],
      z: [{ z: ['drawer', 'modal', 'notification', 'sticky'] }],
      rounded: [{ rounded: ['default'] }],
      leading: [{ leading: ['inherit'] }],
      animate: [
        {
          animate: [
            'fade-in',
            'fade-out',
            'float',
            'fade-in-bottom',
            'fade-in-top',
            'fade-in-left',
            'fade-in-right',
            'fade-out-right',
            'fade-out-left',
            'fade-out-bottom',
            'fade-out-top',
          ],
        },
      ],
    },
  },
});

/**
 * `cn` combines utility functions `clsx` and tailwind-merge, offering the feature of passing styles in a more flexible way, as well as handling style conflicts.
 *
 * ### When to use cn
 * `cn` is already used in all Ragnar components. So if you are passing styles into a Ragnar component at the root, you will never need to use `cn`.
 * Only use `cn` if you are a using native JSX element or third party component, and you are **not** in control of the styles passed into classnames.
 * Overuse of `cn` can have bad impact on performance, so avoid using it when not necessary.
 *
 * ### Examples
 * ```jsx
 * import { cn, clsx } from '@mentimeter/ragnar-tailwind-config';
 *
 * // ❌ Do NOT do this in a Ragnar component
 * <Button className={cn("text-200", classNameFromOuterProps)} />
 *
 * // ✅ Do this instead - use clsx
 * <Button className={clsx("text-200", classNameFromOuterProps)} />
 *
 * // ✅ This is a good use case for `cn`
 * <button className={cn("text-200", classNameFromOuterProps)} />
 *
 * // ❌ With full control over styles, `cn` is unnecessary here
 * <button className={cn("text-200")} />
 *
 * ```
 * */
export function cn(...classes: ClassValue[]) {
  return twMerge(clsx(classes));
}
